import React, { useState, useMemo } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { usePlans } from '@/hooks/usePlans';
import { useEntitlements, useUpdateEntitlements } from '@/hooks/useEntitlements';
import { useBulkWorkspaces } from '@/hooks/useBulkWorkspaces';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Shield, Crown, Settings, Plus, CheckCircle, XCircle, AlertCircle, Building2, Users, Package, Layers, History, Zap, ArrowDownUp, Target } from 'lucide-react';
import { toast } from 'sonner';
import { CustomPlanBuilder } from '@/components/AccessControl/CustomPlanBuilder';
import { WorkspacePickerModal } from '@/components/AccessControl/WorkspacePickerModal';
import { AuditLogViewer } from '@/components/AccessControl/AuditLogViewer';

interface ModuleCardProps {
  module: any;
  isOwner: boolean;
  canManage: boolean;
  onToggle: (moduleId: string, enabled: boolean) => void;
  isUpdating: boolean;
  scope?: 'workspace' | 'org';
}

function ModuleCard({ module, isOwner, canManage, onToggle, isUpdating, scope = 'workspace' }: ModuleCardProps) {
  const isEnabled = module.enabled;
  const source = module.source;
  const moduleId = module.module_id || module.id;
  
  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'owner':
        return <Badge variant="default" className="text-xs">Owner</Badge>;
      case 'plan':
        return <Badge variant="secondary" className="text-xs">Plan</Badge>;
      case 'workspace_override':
        return <Badge variant="outline" className="text-xs">Workspace Override</Badge>;
      case 'org_override':
        return <Badge variant="outline" className="text-xs">Org Override</Badge>;
      default:
        return <Badge variant="destructive" className="text-xs">Disabled</Badge>;
    }
  };

  const canToggle = (canManage || isOwner) && source !== 'owner';
  
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
      <div className="space-y-1 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium">{module.name}</h4>
          {isEnabled ? (
            <CheckCircle className="h-4 w-4 text-success" />
          ) : (
            <XCircle className="h-4 w-4 text-muted-foreground" />
          )}
          {getSourceBadge(source)}
          {scope === 'org' && (
            <Badge variant="outline" className="text-xs bg-accent/20">
              Inherited
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{module.description}</p>
      </div>
      
      {canToggle ? (
        <Switch
          checked={isEnabled}
          onCheckedChange={(checked) => onToggle(moduleId, checked)}
          disabled={isUpdating}
        />
      ) : source === 'owner' ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Switch
                  checked={true}
                  disabled={true}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Owner access bypasses module restrictions</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
}

export default function AccessControlEnhanced() {
  const { currentWorkspace, isOwner } = useWorkspace();
  const { data: plansData, isLoading: plansLoading } = usePlans();
  const { mutate: updateEntitlementsMutate, isPending: isUpdating } = useUpdateEntitlements();
  const { assignPlanToWorkspaces, setOverridesForWorkspaces, isLoading: isBulkLoading } = useBulkWorkspaces();
  
  // Workspace entitlements
  const { 
    data: workspaceEntitlements,
    isLoading: workspaceLoading
  } = useEntitlements('workspace', currentWorkspace?.id || '');
  
  // Organization entitlements (if applicable)
  const { 
    data: orgEntitlements,
    isLoading: orgLoading
  } = useEntitlements('org', currentWorkspace?.organization?.id || '');

  // State management
  const [activeTab, setActiveTab] = useState('workspace');
  const [customPlanOpen, setCustomPlanOpen] = useState(false);
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState(false);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'plan' | 'overrides' | null>(null);
  const [bulkPlanCode, setBulkPlanCode] = useState('');

  console.log('[AccessControlEnhanced] Render:', { 
    workspace: currentWorkspace?.id,
    isOwner,
    workspaceEntitlements: !!workspaceEntitlements,
    orgEntitlements: !!orgEntitlements
  });

  // Mock data for demonstration
  const mockWorkspaces = [
    { id: '1', name: 'Development Team', organization: { name: 'Acme Corp' }, plan: { name: 'Pro' }, member_count: 15 },
    { id: '2', name: 'Marketing Department', organization: { name: 'Acme Corp' }, plan: { name: 'Starter' }, member_count: 8 },
    { id: '3', name: 'Sales Team', organization: { name: 'Acme Corp' }, plan: { name: 'Enterprise' }, member_count: 25 },
  ];

  const mockAuditLogs = [
    {
      id: '1',
      actor_user_id: 'user-1',
      actor_email: 'admin@acme.com',
      scope: 'workspace' as const,
      scope_id: currentWorkspace?.id || '',
      scope_name: currentWorkspace?.name,
      action: 'plan_changed',
      resource_type: 'workspace_plan',
      resource_id: 'plan-1',
      before: { plan_name: 'Starter' },
      after: { plan_name: 'Pro' },
      created_at: new Date().toISOString(),
    },
    {
      id: '2',
      actor_user_id: 'user-1',
      actor_email: 'admin@acme.com',
      scope: 'workspace' as const,
      scope_id: currentWorkspace?.id || '',
      scope_name: currentWorkspace?.name,
      action: 'module_enabled',
      resource_type: 'workspace_module',
      resource_id: 'ai_parser',
      before: { module_name: 'AI Parser', enabled: false },
      after: { module_name: 'AI Parser', enabled: true },
      created_at: new Date(Date.now() - 3600000).toISOString(),
    }
  ];

  // Access control
  if (!workspaceEntitlements && !workspaceLoading && !isOwner) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <Shield className="h-8 w-8 text-muted-foreground mb-2" />
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Only workspace owners and administrators can access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (plansLoading || workspaceLoading || (activeTab === 'organization' && orgLoading)) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading access control...</p>
          </div>
        </div>
      </div>
    );
  }

  const plans = plansData?.plans || [];
  const workspaceModules = workspaceEntitlements?.modules || [];
  const workspaceSubModules = workspaceEntitlements?.sub_modules || [];
  const orgModules = orgEntitlements?.modules || [];
  const currentWorkspacePlan = workspaceEntitlements?.plan;
  const currentOrgPlan = orgEntitlements?.plan;
  const displayIsOwner = workspaceEntitlements?.is_owner || isOwner;

  // Event handlers
  const handlePlanChange = (scope: 'workspace' | 'org', planCode: string) => {
    console.log('[AccessControlEnhanced] Plan change:', { scope, planCode });
    
    updateEntitlementsMutate({
      action: 'update_plan',
      scope,
      scope_id: scope === 'workspace' ? currentWorkspace?.id || '' : currentWorkspace?.organization?.id || '',
      plan_code: planCode
    });
  };

  const handleModuleToggle = (scope: 'workspace' | 'org', moduleId: string, enabled: boolean) => {
    console.log('[AccessControlEnhanced] Module toggle:', { scope, moduleId, enabled });
    
    updateEntitlementsMutate({
      action: 'set_overrides',
      scope,
      scope_id: scope === 'workspace' ? currentWorkspace?.id || '' : currentWorkspace?.organization?.id || '',
      overrides: [{ module_id: moduleId, enabled }]
    });
  };

  const handleSubModuleToggle = (scope: 'workspace' | 'org', subModuleId: string, enabled: boolean) => {
    console.log('[AccessControlEnhanced] Sub-module toggle:', { scope, subModuleId, enabled });
    
    updateEntitlementsMutate({
      action: 'set_sub_overrides',
      scope,
      scope_id: scope === 'workspace' ? currentWorkspace?.id || '' : currentWorkspace?.organization?.id || '',
      sub_overrides: [{ sub_module_id: subModuleId, enabled }]
    });
  };

  const handleCreateCustomPlan = (planData: {
    name: string;
    code: string;
    modules: string[];
    subModules: string[];
  }) => {
    console.log('[AccessControlEnhanced] Creating custom plan:', planData);
    toast.success('Custom plan creation coming soon!');
    setCustomPlanOpen(false);
  };

  const handleBulkOperation = () => {
    if (!bulkAction || selectedWorkspaces.size === 0) {
      toast.error('Select workspaces and an action');
      return;
    }

    console.log('[AccessControlEnhanced] Bulk operation:', { 
      action: bulkAction, 
      workspaces: Array.from(selectedWorkspaces) 
    });

    if (bulkAction === 'plan' && bulkPlanCode) {
      assignPlanToWorkspaces(
        Array.from(selectedWorkspaces),
        bulkPlanCode,
        currentWorkspace?.organization?.id
      );
    }

    setWorkspacePickerOpen(false);
    setSelectedWorkspaces(new Set());
  };

  // Group modules by category
  const modulesByCategory = (modules: any[]) => modules.reduce((acc: any, module: any) => {
    const category = module.name.includes('AI') ? 'AI & Processing' : 
                    module.name.includes('Analytics') || module.name.includes('Reports') ? 'Analytics & Reports' :
                    module.name.includes('Bill') || module.name.includes('Processing') ? 'Bill Management' : 
                    module.name.includes('Collection') ? 'Collections' : 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(module);
    return acc;
  }, {});

  const workspaceModulesByCategory = modulesByCategory(workspaceModules);
  const orgModulesByCategory = modulesByCategory(orgModules);

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Access Control</h1>
          <p className="text-muted-foreground">
            Manage plans and modules for {currentWorkspace?.name}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setCustomPlanOpen(true)}
            className="flex items-center gap-2"
          >
            <Package className="h-4 w-4" />
            Custom Plan
          </Button>
          {displayIsOwner && (
            <Button
              variant="outline"
              onClick={() => setWorkspacePickerOpen(true)}
              className="flex items-center gap-2"
            >
              <Zap className="h-4 w-4" />
              Bulk Operations
            </Button>
          )}
          <Badge variant="outline" className="flex items-center gap-2">
            <Crown className="h-4 w-4" />
            {displayIsOwner ? 'Owner' : 'Admin'}
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="workspace" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Workspace
          </TabsTrigger>
          <TabsTrigger value="organization" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Organization
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workspace" className="space-y-6">
          {/* Current Plan */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Current Plan
              </CardTitle>
              <CardDescription>
                Manage the plan for this workspace
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{currentWorkspacePlan?.name || 'No Plan'}</p>
                    <p className="text-sm text-muted-foreground">
                      {currentWorkspacePlan ? `Includes ${plans.find(p => p.code === currentWorkspacePlan.code)?.modules.length || 0} modules` : 'No active plan'}
                    </p>
                  </div>
                  <div>
                    <Select
                      value={currentWorkspacePlan?.code || ''}
                      onValueChange={(planCode) => handlePlanChange('workspace', planCode)}
                      disabled={!displayIsOwner || isUpdating}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {plans.map((plan) => (
                          <SelectItem key={plan.code} value={plan.code}>
                            {plan.name} ({plan.modules.length} modules)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {currentWorkspacePlan && (
                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-2">Included Modules</h4>
                    <div className="flex flex-wrap gap-2">
                      {plans
                        .find(p => p.code === currentWorkspacePlan.code)
                        ?.modules.map((module) => (
                          <Badge key={module.id} variant="secondary">
                            {module.name}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Module Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Module Access
              </CardTitle>
              <CardDescription>
                {displayIsOwner 
                  ? 'As owner, you have access to all modules. You can also enable additional modules for this workspace.'
                  : 'Manage module access for this workspace. Changes require admin permissions.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(workspaceModulesByCategory).map(([category, categoryModules]: [string, any]) => (
                  <div key={category} className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      {category}
                    </h4>
                    <div className="space-y-3">
                      {categoryModules.map((module: any) => (
                        <ModuleCard
                          key={module.module_id || module.id}
                          module={module}
                          isOwner={displayIsOwner}
                          canManage={workspaceEntitlements?.is_owner || false}
                          onToggle={(moduleId, enabled) => handleModuleToggle('workspace', moduleId, enabled)}
                          isUpdating={isUpdating}
                          scope="workspace"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Sub-Module Management */}
          {workspaceSubModules.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Sub-Module Access</CardTitle>
                <CardDescription>
                  Fine-grained control over module features and capabilities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workspaceSubModules.map((subModule: any) => (
                    <ModuleCard
                      key={subModule.sub_module_id}
                      module={{
                        ...subModule,
                        id: subModule.sub_module_id,
                        module_id: subModule.sub_module_id
                      }}
                      isOwner={displayIsOwner}
                      canManage={workspaceEntitlements?.is_owner || false}
                      onToggle={(subModuleId, enabled) => handleSubModuleToggle('workspace', subModuleId, enabled)}
                      isUpdating={isUpdating}
                      scope="workspace"
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audit Trail */}
          <AuditLogViewer
            scope="workspace"
            scopeId={currentWorkspace?.id || ''}
            logs={mockAuditLogs}
          />
        </TabsContent>

        <TabsContent value="organization" className="space-y-6">
          {/* Organization Plan Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Organization Plan
              </CardTitle>
              <CardDescription>
                Manage the plan for this organization. Changes will inherit to all workspaces.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{currentOrgPlan?.name || 'No Organization Plan'}</p>
                    <p className="text-sm text-muted-foreground">
                      {currentOrgPlan ? `Organization-wide access to ${plans.find(p => p.code === currentOrgPlan.code)?.modules.length || 0} modules` : 'No organization-level plan set'}
                    </p>
                  </div>
                  <div>
                    <Select
                      value={currentOrgPlan?.code || ''}
                      onValueChange={(planCode) => handlePlanChange('org', planCode)}
                      disabled={!displayIsOwner || isUpdating}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select org plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {plans.map((plan) => (
                          <SelectItem key={plan.code} value={plan.code}>
                            {plan.name} ({plan.modules.length} modules)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {currentOrgPlan && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowDownUp className="h-4 w-4 text-accent" />
                      <h4 className="font-medium">Inherited by All Workspaces</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {plans
                        .find(p => p.code === currentOrgPlan.code)
                        ?.modules.map((module) => (
                          <Badge key={module.id} variant="secondary" className="bg-accent/20">
                            {module.name}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Organization Module Overrides */}
          {orgModules.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Organization Module Overrides
                </CardTitle>
                <CardDescription>
                  Override specific modules at the organization level. These settings will apply to all workspaces unless overridden at the workspace level.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(orgModulesByCategory).map(([category, categoryModules]: [string, any]) => (
                    <div key={category} className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                        {category}
                      </h4>
                      <div className="space-y-3">
                        {categoryModules.map((module: any) => (
                          <ModuleCard
                            key={module.module_id || module.id}
                            module={module}
                            isOwner={displayIsOwner}
                            canManage={orgEntitlements?.is_owner || false}
                            onToggle={(moduleId, enabled) => handleModuleToggle('org', moduleId, enabled)}
                            isUpdating={isUpdating}
                            scope="org"
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bulk Workspace Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Bulk Workspace Management
              </CardTitle>
              <CardDescription>
                Apply changes to multiple workspaces simultaneously
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button
                  onClick={() => {
                    setBulkAction('plan');
                    setWorkspacePickerOpen(true);
                  }}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Assign Plan to Multiple Workspaces
                </Button>
                <Button
                  onClick={() => {
                    setBulkAction('overrides');
                    setWorkspacePickerOpen(true);
                  }}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Apply Module Overrides to Multiple Workspaces
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Organization Audit Trail */}
          <AuditLogViewer
            scope="org"
            scopeId={currentWorkspace?.organization?.id || ''}
            logs={mockAuditLogs}
          />
        </TabsContent>
      </Tabs>

      {/* Custom Plan Builder Dialog */}
      <CustomPlanBuilder
        open={customPlanOpen}
        onOpenChange={setCustomPlanOpen}
        modules={workspaceModules.map(m => ({ ...m, id: m.module_id }))}
        subModules={workspaceSubModules.map(sm => ({ ...sm, id: sm.sub_module_id }))}
        onCreatePlan={handleCreateCustomPlan}
      />

      {/* Workspace Picker Modal */}
      <WorkspacePickerModal
        open={workspacePickerOpen}
        onOpenChange={setWorkspacePickerOpen}
        workspaces={mockWorkspaces}
        selectedWorkspaces={selectedWorkspaces}
        onSelectionChange={setSelectedWorkspaces}
        onConfirm={handleBulkOperation}
        title={bulkAction === 'plan' ? 'Select Workspaces for Plan Assignment' : 'Select Workspaces for Module Override'}
        description={bulkAction === 'plan' ? 'Choose which workspaces to assign the new plan to.' : 'Choose which workspaces to apply module overrides to.'}
        isLoading={isBulkLoading}
      />
    </div>
  );
}